import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Search,
  RotateCcw,
  Plus,
  Layers,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { WorkspacePage } from '@/components/layout/WorkspacePage'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import {
  ExcelEditableTable,
  type ExcelColumn,
} from '@/components/table/ExcelEditableTable'
import {
  type WorkshopOverviewItem,
  type WorkshopOverviewResponse,
  fetchWorkshopOverview,
  translateStageError,
} from '@/services/production/stageApi'
import { ProductionOrderDetailDrawer } from './ProductionOrderDetailDrawer'

export interface ProductionProgressRow extends WorkshopOverviewItem, Record<string, unknown> {
  stt: number
  bottleneck_text: string
  actions?: string
}

export interface ProductionProgressPageProps {
  initialOverview?: WorkshopOverviewResponse | null
  initialLoading?: boolean
  initialError?: string | null
  initialStatus?: 'running' | 'completed' | 'all'
  initialSearch?: string
}

export function ProductionProgressPage({
  initialOverview = null,
  initialLoading = false,
  initialError = null,
  initialStatus = 'running',
  initialSearch = '',
}: ProductionProgressPageProps = {}) {
  const { planLineId } = useParams<{ planLineId?: string }>()
  const navigate = useNavigate()

  // Filters & Pagination state
  const [status, setStatus] = useState<'running' | 'completed' | 'all'>(initialStatus)
  const [search, setSearch] = useState(initialSearch)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(30)

  // Data state
  const [overview, setOverview] = useState<WorkshopOverviewResponse | null>(initialOverview)
  const [loading, setLoading] = useState(() => initialLoading || (!initialOverview && !initialError))
  const [error, setError] = useState<string | null>(initialError)

  // Selected order for detail drawer
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(planLineId || null)

  // Sync route param with drawer state
  useEffect(() => {
    if (planLineId) {
      setSelectedOrderId(planLineId)
    }
  }, [planLineId])

  const handleOpenDetail = useCallback(
    (orderId: string) => {
      setSelectedOrderId(orderId)
      navigate(`/production/progress/${encodeURIComponent(orderId)}`, { replace: false })
    },
    [navigate],
  )

  const handleCloseDetail = useCallback(() => {
    setSelectedOrderId(null)
    navigate('/production/progress', { replace: false })
  }, [navigate])

  // Load overview from API
  const loadData = useCallback(
    async (st: 'running' | 'completed' | 'all', q: string, p: number, lim: number) => {
      try {
        setLoading(true)
        setError(null)
        const res = await fetchWorkshopOverview({
          status: st,
          search: q,
          page: p,
          limit: lim,
        })
        setOverview(res)
      } catch (err) {
        console.error('Failed to load workshop overview', err)
        setError(translateStageError(err))
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!initialOverview && !initialError) {
      void loadData(status, search, page, limit)
    }
  }, [initialOverview, initialError, status, search, page, limit, loadData])

  // Table rows formatting
  const rows: ProductionProgressRow[] = useMemo(() => {
    if (!overview?.items) return []
    return overview.items.map((item, idx) => {
      let bnText = '—'
      if (item.bottleneck) {
        const bn = item.bottleneck
        const targetUnits = item.planned_qty * bn.qty_per
        const deficit = bn.deficit_units != null ? bn.deficit_units : Math.max(0, targetUnits - bn.completed_dinh_hinh)

        if (deficit <= 0 || bn.max_sets_possible >= item.planned_qty) {
          bnText = 'Đủ bộ'
        } else {
          const specStr = bn.spec ? ` (${bn.spec})` : ''
          bnText = `${bn.part_name}${specStr} - thiếu ${deficit}`
        }
      }

      return {
        ...item,
        stt: (overview.pagination.page - 1) * overview.pagination.limit + idx + 1,
        bottleneck_text: bnText,
        actions: 'Xem',
      }
    })
  }, [overview])

  // Total sums calculation — ONLY SUM PRODUCT UNITS (SP), NEVER MIX WITH PARTS (CT)
  const totals = useMemo(() => {
    if (!rows || rows.length === 0) {
      return {
        orderCount: 0,
        plannedQty: 0,
        dongGoiQty: 0,
        dongGoiPct: 0,
      }
    }

    const orderCount = overview?.pagination.total || rows.length
    const plannedQty = rows.reduce((sum, r) => sum + (Number(r.planned_qty) || 0), 0)
    const dongGoiQty = rows.reduce(
      (sum, r) => sum + (Number(r.stages?.DONG_GOI?.completed_qty) || 0),
      0,
    )
    const dongGoiPct = plannedQty > 0 ? Math.round((dongGoiQty / plannedQty) * 1000) / 10 : 0

    return {
      orderCount,
      plannedQty,
      dongGoiQty,
      dongGoiPct,
    }
  }, [rows, overview?.pagination.total])

  // Table columns definition with stickyLeft invariant
  const columns: ExcelColumn<ProductionProgressRow>[] = useMemo(
    () => [
      {
        key: 'stt',
        label: 'STT',
        width: 36,
        stickyLeft: 0,
        align: 'center',
        readOnly: true,
      },
      {
        key: 'product_code',
        label: 'MÃ HÀNG',
        width: 115,
        stickyLeft: 36,
        align: 'left',
        readOnly: true,
        renderCell: (val, row) => (
          <button
            type="button"
            onClick={() => handleOpenDetail(row.plan_line_id)}
            className="w-full text-left font-bold text-blue-700 hover:text-blue-900 hover:underline"
            title={`Xem chi tiết ${String(val || '')}`}
          >
            <>
              <span className="block truncate">{String(val || '—')}</span>
              <span className="block truncate text-[10px] font-medium text-slate-500">{String(row.customer_name || '—')}</span>
            </>
          </button>
        ),
      },
      {
        key: 'planned_qty',
        label: 'KẾ HOẠCH',
        width: 60,
        stickyLeft: 151,
        align: 'right',
        readOnly: true,
        formatter: (val) => `${Number(val || 0).toLocaleString()} SP`,
      },
      {
        key: 'PHOI' as any,
        label: '1. Phôi',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.PHOI
          if (!s) return <span className="text-slate-400">—</span>
          const unitsText = `${s.completed_units ?? 0}/${s.required_units ?? 0} CT`
          const setsText = `(${s.equivalent_sets ?? 0} bộ)`
          return (
            <div className="flex flex-col items-center justify-center text-xs leading-tight">
              <span className="font-medium text-slate-800">{unitsText}</span>
              <span className="text-[10px] text-slate-500 font-mono">{setsText}</span>
            </div>
          )
        },
      },
      {
        key: 'DINH_HINH' as any,
        label: '2. Đ.hình',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.DINH_HINH
          if (!s) return <span className="text-slate-400">—</span>
          const unitsText = `${s.completed_units ?? 0}/${s.required_units ?? 0} CT`
          const setsText = `(${s.equivalent_sets ?? 0} bộ)`
          return (
            <div className="flex flex-col items-center justify-center text-xs leading-tight">
              <span className="font-medium text-slate-800">{unitsText}</span>
              <span className="text-[10px] text-slate-500 font-mono">{setsText}</span>
            </div>
          )
        },
      },
      {
        key: 'LAP_RAP' as any,
        label: '3. Lắp ráp',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.LAP_RAP
          const completed = s?.completed_qty ?? 0
          const planned = row.planned_qty || 1
          const pct = Math.min(100, Math.round((completed / planned) * 100))
          const qtyText = `${completed}/${row.planned_qty} SP`
          return (
            <div className="w-full px-1 flex flex-col items-center justify-center text-xs">
              <span className="font-medium text-slate-800">{qtyText}</span>
              <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        key: 'CHA_NHAM' as any,
        label: '4. Nhám',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.CHA_NHAM
          const completed = s?.completed_qty ?? 0
          const planned = row.planned_qty || 1
          const pct = Math.min(100, Math.round((completed / planned) * 100))
          const qtyText = `${completed}/${row.planned_qty} SP`
          return (
            <div className="w-full px-1 flex flex-col items-center justify-center text-xs">
              <span className="font-medium text-slate-800">{qtyText}</span>
              <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        key: 'SON' as any,
        label: '5. Sơn',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.SON
          const completed = s?.completed_qty ?? 0
          const planned = row.planned_qty || 1
          const pct = Math.min(100, Math.round((completed / planned) * 100))
          const qtyText = `${completed}/${row.planned_qty} SP`
          return (
            <div className="w-full px-1 flex flex-col items-center justify-center text-xs">
              <span className="font-medium text-slate-800">{qtyText}</span>
              <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        key: 'FITTING' as any,
        label: '6. Fitting',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.FITTING
          const completed = s?.completed_qty ?? 0
          const planned = row.planned_qty || 1
          const pct = Math.min(100, Math.round((completed / planned) * 100))
          const qtyText = `${completed}/${row.planned_qty} SP`
          return (
            <div className="w-full px-1 flex flex-col items-center justify-center text-xs">
              <span className="font-medium text-slate-800">{qtyText}</span>
              <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        key: 'DONG_GOI' as any,
        label: '7. Gói',
        width: 90,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => {
          const s = row.stages?.DONG_GOI
          const completed = s?.completed_qty ?? 0
          const planned = row.planned_qty || 1
          const pct = Math.min(100, Math.round((completed / planned) * 100))
          const qtyText = `${completed}/${row.planned_qty} SP`
          return (
            <div className="w-full px-1 flex flex-col items-center justify-center text-xs">
              <span className="font-medium text-slate-800">{qtyText}</span>
              <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        key: 'bottleneck_text',
        label: 'LƯU Ý',
        width: 145,
        align: 'left',
        readOnly: true,
        multiline: true,
        renderCell: (val, row) => {
          const str = String(val || '')
          const warningCount = Number(row.warnings_count || 0)
          const warning = warningCount > 0 ? `${warningCount} cảnh báo` : null
          if (str === 'Đủ bộ') {
            return (
              <div className="flex flex-col gap-0.5">
                <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800">Đủ bộ</span>
                {warning && <Badge tone="warning" className="w-fit px-1.5 py-0.5 text-[10px] font-semibold">{warning}</Badge>}
              </div>
            )
          }
          if (str === '—') return warning ? <Badge tone="warning" className="px-1.5 py-0.5 text-[10px] font-semibold">{warning}</Badge> : <span className="text-slate-400">—</span>
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-amber-900" title={str}>{str}</span>
              {warning && <Badge tone="warning" className="w-fit px-1.5 py-0.5 text-[10px] font-semibold">{warning}</Badge>}
            </div>
          )
        },
      },
      {
        key: 'actions',
        label: 'THAO TÁC',
        width: 65,
        align: 'center',
        readOnly: true,
        renderCell: (_, row) => (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleOpenDetail(row.plan_line_id)}
            className="h-6 px-2 text-xs font-semibold"
          >
            Chi tiết
          </Button>
        ),
      },
    ],
    [handleOpenDetail],
  )

  const emptyRowFactory = useCallback(
    (): ProductionProgressRow => ({
      id: '',
      plan_line_id: '',
      order_no: null,
      customer_name: null,
      product_code: null,
      product_name: null,
      variant_name: null,
      planned_qty: 0,
      active: true,
      done: false,
      stages: {} as any,
      bottleneck: null,
      warnings_count: 0,
      stt: 0,
      bottleneck_text: '—',
    }),
    [],
  )

  const totalPages = overview?.pagination.total_pages || 1

  return (
    <WorkspacePage
      header={
        <div className="space-y-2">
          <Breadcrumb
            items={[
              { label: 'Trang chủ', to: '/' },
              { label: 'Sản xuất' },
              { label: 'Tiến độ toàn xưởng' },
            ]}
          />
          <PageHeader
            title="Tiến độ toàn xưởng"
            subtitle="Bảng theo dõi tiến độ sản xuất 7 công đoạn cho các lệnh sản xuất trong xưởng."
            actions={
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void loadData(status, search, page, limit)}
                  disabled={loading}
                  className="gap-1.5"
                >
                  <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Làm mới</span>
                </Button>
                <Link to="/production/record">
                  <Button size="sm" variant="primary" className="gap-1.5 font-semibold">
                    <Plus className="h-4 w-4" />
                    <span>Ghi nhận sản lượng</span>
                  </Button>
                </Link>
              </div>
            }
          />
        </div>
      }
      toolbar={
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm">
          {/* Status filter tabs */}
          <div className="flex items-center gap-1">
            <span className="mr-1 font-bold text-slate-600 uppercase text-[11px]">Trạng thái:</span>
            <button
              type="button"
              onClick={() => {
                setStatus('running')
                setPage(1)
              }}
              className={`rounded-md px-2.5 py-1 font-semibold transition-colors ${
                status === 'running'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Đang chạy
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus('completed')
                setPage(1)
              }}
              className={`rounded-md px-2.5 py-1 font-semibold transition-colors ${
                status === 'completed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Hoàn thành
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus('all')
                setPage(1)
              }}
              className={`rounded-md px-2.5 py-1 font-semibold transition-colors ${
                status === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Tất cả
            </button>
          </div>

          {/* Search box & Pagination */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                aria-label="Tìm kiếm lệnh sản xuất"
                placeholder="Tìm mã hàng, khách hàng, số đơn..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                className="h-8 w-60 rounded-md border border-slate-300 py-1 pl-8 pr-2.5 text-xs focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1.5 text-slate-600">
              <span>
                Trang {page} / {totalPages}
              </span>
              <button
                type="button"
                aria-label="Trang trước"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-slate-300 p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Trang sau"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded border border-slate-300 p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <select
                aria-label="Số dòng trên trang"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value))
                  setPage(1)
                }}
                className="h-8 rounded border border-slate-300 bg-white px-2 text-xs focus:outline-none"
              >
                <option value={30}>30 dòng</option>
                <option value={50}>50 dòng</option>
                <option value={100}>100 dòng</option>
              </select>
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-700">
          <div className="flex flex-wrap items-center gap-4">
            <span>
              <strong>Tổng số:</strong> {totals.orderCount} lệnh
            </span>
            <span className="text-slate-300">|</span>
            <span>
              <strong>Kế hoạch:</strong> {totals.plannedQty.toLocaleString()} SP
            </span>
            <span className="text-slate-300">|</span>
            <span>
              <strong>Đã đóng gói:</strong> {totals.dongGoiQty.toLocaleString()} SP ({totals.dongGoiPct}%)
            </span>
          </div>
          <div className="text-[11px] text-slate-500">
            * Cột Phôi và Định hình tính theo chi tiết (CT); Lắp ráp đến Đóng gói tính theo sản phẩm (SP).
          </div>
        </div>
      }
    >
      {/* Error display */}
      {error && (
        <div className="m-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="font-semibold text-red-900">Không thể tải bảng tiến độ:</p>
            <p className="mt-0.5">{error}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void loadData(status, search, page, limit)}
              className="mt-2"
            >
              Thử lại
            </Button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && rows.length === 0 && !error && (
        <div className="flex h-64 items-center justify-center text-sm text-slate-500">
          Đang tải dữ liệu tiến độ toàn xưởng...
        </div>
      )}

      {/* Empty State: EXACT instruction required by Product Owner */}
      {!loading && rows.length === 0 && !error && (
        <div className="flex h-full flex-col items-center justify-center p-8 text-center bg-white rounded-lg border border-dashed border-slate-200 m-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 mb-3">
            <Layers className="h-6 w-6" />
          </div>
          <h3 className="text-sm font-bold text-slate-800 mb-1">Chưa có lệnh sản xuất</h3>
          <p className="max-w-md text-xs text-slate-600 leading-relaxed mb-4">
            Chưa có lệnh sản xuất. Tạo lệnh ở Kế hoạch → Lệnh sản xuất, và kích hoạt định mức mộc cho mã hàng.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link to="/planning/production-orders">
              <Button size="sm" variant="primary" className="text-xs">
                Kế hoạch → Lệnh sản xuất
              </Button>
            </Link>
            <Link to="/catalog/bom-aggregate">
              <Button size="sm" variant="secondary" className="text-xs">
                Tổng hợp Định mức (BOM)
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Table display */}
      {rows.length > 0 && (
        <ExcelEditableTable<ProductionProgressRow>
          fill
          variant="sheet"
          rowHeight={34}
          density="compact"
          columns={columns}
          data={rows}
          onChange={() => {}}
          emptyRowFactory={emptyRowFactory}
          readOnly={true}
          showActions={false}
          showAddRowButton={false}
          onRowFocus={(row) => handleOpenDetail(row.plan_line_id)}
          emptyMessage="Không có dòng phù hợp."
          summaryRow={(visible, stickyOffsets, visibleColumns) => {
            const visiblePlanned = visible.reduce((sum, r) => sum + (Number(r.planned_qty) || 0), 0)
            const visibleDongGoi = visible.reduce(
              (sum, r) => sum + (Number(r.stages?.DONG_GOI?.completed_qty) || 0),
              0,
            )
            const visibleLapRap = visible.reduce(
              (sum, r) => sum + (Number(r.stages?.LAP_RAP?.completed_qty) || 0),
              0,
            )
            const pct = visiblePlanned > 0 ? Math.round((visibleDongGoi / visiblePlanned) * 1000) / 10 : 0

            return (
              <tr aria-label="Tổng" className="excel-sheet-summary">
                {(visibleColumns ?? columns).map((column) => {
                  const isSticky = column.stickyLeft !== undefined
                  let cellContent: string = ''

                  if (column.key === 'stt') {
                    cellContent = 'TỔNG'
                  } else if (column.key === 'product_code') {
                    cellContent = `${visible.length} lệnh`
                  } else if (column.key === 'planned_qty') {
                    cellContent = `${visiblePlanned.toLocaleString()} SP`
                  } else if (column.key === 'LAP_RAP') {
                    cellContent = `${visibleLapRap.toLocaleString()} SP`
                  } else if (column.key === 'DONG_GOI') {
                    cellContent = `${visibleDongGoi.toLocaleString()} SP (${pct}%)`
                  } else if (column.key === 'PHOI' || column.key === 'DINH_HINH') {
                    cellContent = '—'
                  }

                  return (
                    <td
                      key={column.key}
                      data-column-key={column.key}
                      data-frozen={isSticky ? 'left' : undefined}
                      style={
                        isSticky
                          ? {
                              position: 'sticky',
                              left: stickyOffsets?.[column.key]?.left ?? 0,
                              zIndex: 9,
                            }
                          : undefined
                      }
                      className={`${isSticky ? 'excel-sheet-pinned' : ''} ${
                        column.align === 'right'
                          ? 'text-right'
                          : column.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      } px-1.5 py-1 text-xs font-bold text-slate-800`}
                    >
                      {cellContent}
                    </td>
                  )
                })}
              </tr>
            )
          }}
        />
      )}

      {/* Order Detail Drawer */}
      {selectedOrderId && (
        <ProductionOrderDetailDrawer
          planLineId={selectedOrderId}
          onClose={handleCloseDetail}
        />
      )}
    </WorkspacePage>
  )
}
